import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import type { TemplateChoice } from "work/client-proposal-templates/_views/template-choices";

type TemplateChoiceFieldProps = {
  label: string;
  helperText: string;
  choices: readonly TemplateChoice[];
  disabled: boolean;
  loading: boolean;
  /** The chosen id, or `""` for nothing chosen. */
  value: string;
  onChange: (id: string) => void;
};

type TemplateChoiceMultiFieldProps = Omit<TemplateChoiceFieldProps, "value" | "onChange"> & {
  value: readonly string[];
  onChange: (ids: string[]) => void;
};

const NOTHING_TO_CHOOSE = "None saved yet";

/**
 * One thing chosen from a list, by name.
 *
 * An `Autocomplete` rather than a `select` because these lists are an organization's whole library
 * of templates, and a reader who knows the name should be able to type it instead of scrolling for
 * it. It filters what it was given rather than asking the server again: the page holds a page of
 * each list and folds the saved choice into it (see `buildChoices`), which is enough for a screen
 * somebody opens once a quarter and avoids a query on every keystroke.
 *
 * Clearable, because "none" is a real answer here. A client that has not chosen a conflicts
 * template has not made a mistake — the gap the page reports names the two that actually stop a
 * proposal being built.
 */
export default function TemplateChoiceField(props: Readonly<TemplateChoiceFieldProps>) {
  const selected = props.choices.find((choice) => choice.id === props.value) ?? null;

  return (
    <Autocomplete
      options={props.choices as TemplateChoice[]}
      value={selected}
      disabled={props.disabled}
      loading={props.loading}
      getOptionLabel={(choice) => choice.name}
      isOptionEqualToValue={(choice, other) => choice.id === other.id}
      noOptionsText={NOTHING_TO_CHOOSE}
      onChange={(_event, choice) => props.onChange(choice?.id ?? "")}
      renderInput={(parameters) => (
        <TextField {...parameters} label={props.label} helperText={props.helperText} />
      )}
    />
  );
}

/**
 * Several things chosen from a list, in the order they were picked.
 *
 * The order is kept rather than sorted, because the roll-out copies the policy templates onto the
 * proposal in the order they are stored — so what a client arranges here is what a firm reads.
 */
export function TemplateChoiceMultiField(props: Readonly<TemplateChoiceMultiFieldProps>) {
  const selected = props.value
    .map((id) => props.choices.find((choice) => choice.id === id))
    .filter((choice): choice is TemplateChoice => choice !== undefined);

  return (
    <Autocomplete
      multiple
      options={props.choices as TemplateChoice[]}
      value={selected}
      disabled={props.disabled}
      loading={props.loading}
      getOptionLabel={(choice) => choice.name}
      isOptionEqualToValue={(choice, other) => choice.id === other.id}
      noOptionsText={NOTHING_TO_CHOOSE}
      onChange={(_event, choices) => props.onChange(choices.map((choice) => choice.id))}
      renderInput={(parameters) => (
        <TextField {...parameters} label={props.label} helperText={props.helperText} />
      )}
    />
  );
}
